import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, logout } from '../lib/auth-client';
import { useSession } from '../contexts/SessionContext';
// Trilha 3 Metade B — escrita cifrada real (substitui o placeholder base64). A
// justificativa é cifrada no navegador com a DEK do caso (AAD=caseId); o servidor
// nunca vê o texto. Gateado pela leitura: sem material (flag OFF / sem keypair /
// sem acesso) o parecer NÃO é emitido (não gravamos placeholder — ADR-0008).
import { resolveCaseDekAccess, encryptCaseFields, type CaseDekAccess } from '../lib/case-crypto';
import { kekMortaNestaSessao } from '../lib/kek-member-session';
import { Alert, Button, Card, Field, Textarea } from '@aions/ui-primitives';

type Voto = 'procedente' | 'improcedente' | 'improcedente_insuficiencia';

export function ParecerPage({
  caseId,
  onBack,
  onDone,
}: {
  caseId: string;
  onBack: () => void;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { session, setSession } = useSession();
  const [voto, setVoto] = useState<Voto>('procedente');
  const [justificativa, setJustificativa] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Código do bloqueio da IA-Auditora (auditoraReason do 422), mapeado p/ texto
  // na renderização do erro — interpola o {{reason}} de parecer.errors.auditora_block.
  const [auditoraReason, setAuditoraReason] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // null = ainda carregando o material do caso (GET /cases/:id no mount, A-opt-1).
  const [access, setAccess] = useState<CaseDekAccess | null>(null);
  // Regra 1 — a KEK morreu nesta sessão (F5 / descarte de aba). Detectado no MOUNT,
  // ANTES de o membro digitar: descobrir só no clique do "emitir" custa o
  // retrabalho inteiro (ele redige tudo de novo e só então leva erro).
  const [sessaoExpirada, setSessaoExpirada] = useState(false);

  // A-opt-1: a página resolve seu próprio material (não eleva estado no App).
  useEffect(() => {
    let cancelled = false;
    let sondar = false;
    void (async () => {
      try {
        const resp = await apiFetch(`/api/comite/cases/${caseId}`);
        if (!resp.ok) {
          // 412 (conflito) / 404 / flag OFF → sem material → bloqueia a emissão.
          if (!cancelled) setAccess({ status: 'sem_keypair' });
          return;
        }
        const json = (await resp.json()) as {
          myWrappedPrivateKey?: { ciphertext: string; nonce: string } | null;
          dekWrappedByMembers?: Array<{ memberId: string; wrappedDek: string }> | null;
        };
        const resolvido = resolveCaseDekAccess(json, session?.user.id ?? '');
        if (cancelled) return;
        setAccess(resolvido);
        sondar = resolvido.status === 'ok';
      } catch {
        if (!cancelled) setAccess({ status: 'sem_keypair' });
      }
      // SONDA da KEK (regra 1) — FORA do try acima, de propósito. Dentro dele, uma
      // exceção da sonda viraria `sem_keypair`, que é DIAGNÓSTICO ERRADO:
      // sem_keypair significa "o membro não tem chave nenhuma", não "a sonda
      // falhou". O try segue cobrindo exatamente o que sempre cobriu (o GET e a
      // resolução do acesso). `kekMortaNestaSessao` nunca lança e só bloqueia com
      // determinação positiva — aviso antecipado, não gate.
      if (sondar && !cancelled) {
        const morta = await kekMortaNestaSessao();
        if (!cancelled && morta) setSessaoExpirada(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, session]);

  const leituraIndisponivel = access !== null && access.status !== 'ok';

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!access || access.status !== 'ok') {
      setError('leitura_indisponivel');
      return;
    }
    setSubmitting(true);
    setError(null);
    setAuditoraReason(null);
    try {
      // Cifra real: justificativa → ciphertext+nonce (AAD=caseId). NUNCA placeholder.
      let enc;
      try {
        enc = await encryptCaseFields(access, caseId, { justificativa });
      } catch {
        setError('encrypt_failed');
        return;
      }
      const resp = await apiFetch(`/api/comite/cases/${caseId}/parecer`, {
        method: 'POST',
        body: JSON.stringify({
          voto,
          justificativaCiphertext: enc.justificativa.ciphertext,
          nonce: enc.justificativa.nonce,
        }),
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as {
          error?: string;
          auditoraReason?: string;
        };
        setError(body.error ?? 'Erro ao emitir parecer.');
        setAuditoraReason(body.auditoraReason ?? null);
        return;
      }
      onDone();
    } catch {
      setError('network');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} style={{ marginBottom: 16 }}>
        ← {t('case_detail.back')}
      </Button>
      <Card style={{ maxWidth: 720 }}>
        <h2>{t('parecer.title')}</h2>
        {sessaoExpirada && (
          <Alert
            variant="warning"
            role="status"
            aria-live="polite"
            title={t('case_detail.sessao_expirada_title')}
            style={{ marginBottom: 16 }}
          >
            <div>{t('case_detail.sessao_expirada_body')}</div>
            {/* Incondicional, e é uma DECLARAÇÃO DE CONSEQUÊNCIA, não instrução.
                "Copie o texto" assumiria que existe texto — condição que pode errar
                (a sonda é assíncrona e o campo fica ativo até ela responder: há
                janela real para digitar antes do veredito, provado nos testes
                `sonda-tardia`). "Descarta qualquer texto não enviado" é verdade com
                ou sem rascunho, então nunca mente. */}
            <div style={{ marginTop: 6 }}>{t('case_detail.sessao_expirada_descarte')}</div>
            <div style={{ marginTop: 10 }}>
              <Button
                variant="primary"
                size="sm"
                data-testid="sessao-expirada-entrar"
                onClick={() => {
                  void (async () => {
                    await logout();
                    setSession(null);
                  })();
                }}
              >
                {t('case_detail.sessao_expirada_acao')}
              </Button>
            </div>
          </Alert>
        )}
        {leituraIndisponivel && (
          <Alert variant="warning" role="status" style={{ marginBottom: 16 }}>
            {t('parecer.leitura_indisponivel_aviso')}
          </Alert>
        )}
        {error && (
          <Alert variant="error" style={{ marginBottom: 16 }}>
            {/* Mapeia auditoraReason → texto (reusa as chaves do banner IA-Auditora)
                p/ interpolar o {{reason}} de parecer.errors.auditora_block. */}
            {t(`parecer.errors.${error}`, {
              defaultValue: error,
              reason:
                auditoraReason === 'oitiva_ausente_e_dispensa_nao_justificada'
                  ? t('ia_auditora.banner.reason_oitiva_ausente')
                  : auditoraReason === 'conflito_de_interesse_pendente'
                    ? t('ia_auditora.banner.reason_conflito_pendente')
                    : (auditoraReason ?? t('ia_auditora.banner.block_description')),
            })}
          </Alert>
        )}
        <form onSubmit={(e) => void handleSubmit(e)}>
          {/* Contexto do voto cego onde ele MUDA o comportamento: quem vai escrever
              a fundamentação precisa saber que o texto fica selado até o quórum
              fechar. Restatement de `redactPareceresBlindVote`, não promessa nova. */}
          <p className="parecer-selo">
            {t('parecer.selo_voto_cego', {
              defaultValue: 'Seu voto e sua fundamentação ficam selados até o quórum fechar',
            })}
          </p>

          {/* Cada desfecho é uma SUPERFÍCIE DE ESCOLHA, não um item de lista — o
              alvo clicável é o cartão inteiro. O radio nativo fica no DOM de
              propósito: teclado, leitor de tela e foco vêm corretos de graça, e o
              estado visual sai do próprio `:checked` via `:has()` (uma fonte só). */}
          <div className="cf-field" role="radiogroup" aria-label={t('parecer.voto_label')}>
            <span className="cf-field__label">{t('parecer.voto_label')}</span>
            <div className="parecer-votos">
              {(['procedente', 'improcedente', 'improcedente_insuficiencia'] as const).map((v) => (
                <label key={v} className="parecer-voto">
                  <input
                    type="radio"
                    name="voto"
                    value={v}
                    checked={voto === v}
                    onChange={() => setVoto(v)}
                  />
                  <span className="parecer-voto__label">{t(`parecer.votos.${v}`)}</span>
                </label>
              ))}
            </div>
          </div>

          <Field label={t('parecer.justificativa_label')}>
            {({ id }) => (
              <>
                <Textarea
                  id={id}
                  value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                  minLength={50}
                  required
                  placeholder={t('parecer.justificativa_placeholder', {
                    defaultValue: 'Fundamente sua conclusão com base no que foi apurado',
                  })}
                />
                {/* O mínimo de 50 só falava no SUBMIT, pela mensagem nativa do
                    navegador — depois de a pessoa ter escrito. Agora fala enquanto
                    escreve. `aria-live="polite"` porque muda a cada tecla. */}
                <p
                  className={`parecer-contador${justificativa.trim().length < 50 ? ' parecer-contador--falta' : ''}`}
                  aria-live="polite"
                >
                  {justificativa.trim().length < 50
                    ? t('parecer.faltam_caracteres', {
                        faltam: 50 - justificativa.trim().length,
                        defaultValue:
                          'Faltam {{faltam}} caracteres para o mínimo de fundamentação.',
                      })
                    : t('parecer.minimo_ok', {
                        count: justificativa.trim().length,
                        defaultValue: '{{count}} caracteres — mínimo atendido.',
                      })}
                </p>
              </>
            )}
          </Field>

          <Button
            type="submit"
            loading={submitting}
            disabled={access === null || leituraIndisponivel || sessaoExpirada}
          >
            {t('parecer.submit')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
